import {
  HTTP_INTERCEPTORS,
  HttpClientModule,
  provideHttpClient,
  withFetch,
} from '@angular/common/http';
import { isPlatformBrowser } from '@angular/common';
import { inject, NgModule, PLATFORM_ID } from '@angular/core';
import {
  BrowserModule,
  provideClientHydration,
} from '@angular/platform-browser';
import { AppComponent } from './app.component';
import { DocumentationModule } from './pages/documentation/documentation.module'; // Add this import

import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getAuth, provideAuth } from '@angular/fire/auth';

import { getMessaging, provideMessaging } from '@angular/fire/messaging';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { provideAnimations } from '@angular/platform-browser/animations';
import { CategoriesFacade } from '@sh/categories/facades/categories.facade';
import { CategoriesService } from '@sh/categories/services/categories.service';
import { TagsService } from '@sh/content/services/tags/tags.service';
import { environment } from '@sh/env/environment';
import {
  ApplicationLogLevel,
  LoggingService,
  loggingServiceFactory,
} from '@sh/services/logging/logging.service';
import { NOTYF, notyfFactory } from '@sh/services/toast/toast.service';

import { AppRoutingModule } from './app-routing.module';
import { FooterComponent } from './components/footer/footer.component';
import { HeaderComponent } from './components/header/header.component';
import { LearningLoaderComponent } from './components/learning-loader/learning-loader.component';
import { RoleSelectListComponent } from './components/role-select-list/role-select-list.component';
import { SidebarComponent } from './components/sidebar/sidebar.component';
import { AuthErrorInterceptor } from './interceptors/auth-error.interceptor';
import { AuthInterceptor } from './interceptors/auth.interceptor';
import { LoggingInterceptor } from './interceptors/logging.interceptor';
import { ContentModule } from './modules/content/content.module';
import { SharedUiModule } from './modules/shared-ui/shared-ui.module';
import { AboutComponent } from './pages/about/about.component';
import { LoginComponent } from './pages/auth/login/login.component';
import { ContactComponent } from './pages/contact/contact.component';
import { HomeComponent } from './pages/home/home.component';
import { NewsComponent } from './pages/news/news.component';
import { PrivacyComponent } from './pages/privacy/privacy.component';
import { ProfileComponent } from './pages/profile/profile.component';
import { TermsComponent } from './pages/terms/terms.component';
import { HasRolePipe } from './pipes/has-role.pipe';

@NgModule({
  declarations: [AppComponent],
  imports: [
    AppRoutingModule,
    BrowserModule.withServerTransition({ appId: 'study-heroes' }),
    ContentModule,
    provideFirebaseApp(() => initializeApp(environment.firebaseConfig)),
    provideAuth(() => getAuth()),
    HttpClientModule,
    provideMessaging(() =>
      isPlatformBrowser(inject(PLATFORM_ID)) ? getMessaging() : (null as any),
    ),
    FormsModule,
    ReactiveFormsModule,
    SharedUiModule,
    HomeComponent,
    AboutComponent,
    ContactComponent,
    ProfileComponent,
    TermsComponent,
    PrivacyComponent,
    NewsComponent,
    HeaderComponent,
    LearningLoaderComponent,
    FooterComponent,
    SidebarComponent,
    LoginComponent,
    RoleSelectListComponent,
    DocumentationModule,
    HasRolePipe,
  ],

  providers: [
    provideAnimations(),
    provideClientHydration(),
    provideHttpClient(withFetch()),
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthErrorInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: LoggingInterceptor, multi: true },
    { provide: NOTYF, useFactory: notyfFactory, deps: [PLATFORM_ID] },
    {
      provide: LoggingService,
      useFactory: () => loggingServiceFactory(ApplicationLogLevel.DEBUG),
    },
    CategoriesService,
    CategoriesFacade,
    TagsService,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  routes = [
    { path: '', name: 'Home' },
    { path: 'login', name: 'Login' },
    { path: 'profile', name: 'Profile' },
    { path: 'about', name: 'About' },
    { path: 'contact', name: 'Contact' },
    { path: 'news', name: 'News' },
    { path: 'privacy', name: 'Privacy' },
    { path: 'terms', name: 'Terms' },
    { path: 'books', name: 'Books' },
    { path: 'users', name: 'Users' },
    { path: 'flags', name: 'Flags' },
    { path: 'formats', name: 'Formats' },
    { path: 'resources', name: 'Resources' },
    { path: 'shelves', name: 'Shelves' },
    { path: 'categories', name: 'Categories' },
    { path: 'get-involved', name: 'Get Involved' },
    { path: 'create-resource', name: 'Create Resource' },
    { path: 'documentation', name: 'Documentation' },
  ];
}
